/**
 * DealPicker — §35: the DEAL filter's search-and-pick control on the
 * EntityPicker core, over GET /v1/sales/deals?search= — the ONE text
 * parameter the deals list gained for it (title OR company, the contacts
 * list's shape). It asks the SERVER, never filters the capped list
 * client-side: the Archive comment's trap ("quietly wrong once open deals
 * crowd the cap") is exactly what a picker over the 200-row list would be.
 * A FILTER value: the id travels; the text is what was picked; typing after
 * a pick clears the id. dealId '' = no filter, whatever the text says.
 *
 * Strings differ from the Pipeline's on purpose: its lane says "No matching
 * deals" and its box "Search deals…" — a shared sentence for two conditions
 * is an operability defect (the §35.5 census).
 */
import { fetchDeals } from '../../api'
import type { SalesDeal } from '../../types'
import { EntityPicker } from './EntityPicker'

export interface DealPick {
  /** '' = no filter. */
  dealId: string
  text: string
}

export function DealPicker({ value, onChange, className, id, placeholder }: {
  value: DealPick
  onChange: (next: DealPick) => void
  className: string
  id?: string
  placeholder?: string
}) {
  return (
    <EntityPicker<SalesDeal>
      id={id} className={className} placeholder={placeholder ?? 'Search deals by title or company…'}
      text={value.text}
      onTextChange={t => onChange({ dealId: '', text: t })}
      search={q => fetchDeals({ search: q, limit: 8 })}
      rowKey={d => d.id}
      selectedKey={value.dealId || undefined}
      renderRow={d => (
        <>
          <span className="text-13 font-medium text-ink-primary truncate">{d.name}</span>
          <span className="text-11 text-ink-faint whitespace-nowrap truncate">{d.company !== '' ? `${d.company} · ${d.stage}` : d.stage}</span>
        </>
      )}
      onPick={d => onChange({ dealId: d.id, text: d.name })}
      emptyPrompt="Type to search deals"
      noMatch="No matching deal"
    />
  )
}
